import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { API_BASE_URL } from "../../config";
import { formatINR } from "../../utils/money";
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 15,
    textAlign: 'center',
    borderBottom: '2 solid #000',
    paddingBottom: 10,
  },
  companyName: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 3,
  },
  documentTitle: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  profileSection: {
    flexDirection: 'row',
    marginBottom: 15,
    border: '1 solid #000',
    padding: 10,
  },
  profileImage: {
    width: 100,
    height: 100,
    marginRight: 15,
    border: '1 solid #000',
    borderRadius: 4,
    objectFit: 'cover',
  },
  profileImagePlaceholder: {
    width: 80,
    height: 80,
    marginRight: 15,
    border: '1 solid #000',
    borderRadius: 4,
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileInfo: {
    flex: 1,
  },
  profileText: {
    fontSize: 10,
    marginBottom: 2,
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
    borderBottom: '1 solid #000',
    paddingBottom: 3,
  },
  table: {
    width: '100%',
    border: '1 solid #000',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #000',
  },
  tableRowLast: {
    borderBottom: 'none',
  },
  tableCell: {
    padding: 5,
    fontSize: 10,
    borderRight: '1 solid #000',
    flex: 1,
    justifyContent: 'center',
  },
  tableCellLast: {
    borderRight: 'none',
  },
  tableCellHeader: {
    fontWeight: 'bold',
    backgroundColor: '#f0f0f0',
  },
  emiTable: {
    width: '100%',
    border: '1 solid #000',
    marginTop: 5,
  },
  emiTableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #000',
  },
  emiTableCol: {
    padding: 4,
    fontSize: 9,
    borderRight: '1 solid #000',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emiTableColLast: {
    borderRight: 'none',
  },
  emiHeaderText: {
    fontWeight: 'bold',
    backgroundColor: '#f0f0f0',
  },
  signatures: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    border: '1 solid #000',
    padding: 15,
  },
  signatureBox: {
    width: '30%',
    alignItems: 'center',
    minHeight: 40,
  },
  signatureLabel: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 15,
  },
  signatureLine: {
    width: '100%',
    borderBottom: '1 solid #000',
  },
  footer: {
    marginTop: 15,
    fontSize: 8,
    textAlign: 'center',
    borderTop: '1 solid #000',
    paddingTop: 10,
  },
  divider: {
    borderTop: '1 solid #000',
    marginVertical: 10,
  },
});

const TableRow = ({ cells, isHeader = false, isLast = false }) => (
  <View style={[styles.tableRow, isLast && styles.tableRowLast]}>
    {cells.map((cell, index) => (
      <View
        key={index}
        style={[
          styles.tableCell,
          isHeader && styles.tableCellHeader,
          index === cells.length - 1 && styles.tableCellLast
        ]}
      >
        <Text>{cell || ''}</Text>
      </View>
    ))}
  </View>
);

const EMITableRow = ({ cells, isHeader = false, isLast = false }) => (
  <View style={[styles.emiTableRow, isLast && styles.tableRowLast]}>
    {cells.map((cell, index) => (
      <View
        key={index}
        style={[
          styles.emiTableCol,
          isHeader && styles.emiHeaderText,
          index === cells.length - 1 && styles.emiTableColLast
        ]}
      >
        <Text>{cell}</Text>
      </View>
    ))}
  </View>
);

const BusinessLoanPDF = ({
  personalDetailsData = {},
  verificationDetailsData = {},
  memberDetailsData = {},
  nomineeDetailsData = {},
  memberBusinessDetailsData = {},
  nomineeBusinessDetailsData = {},
  loanDetailsData = {},
  proposedLoanDetailsData = {},
  bankDetailsData = {},
  // Real loan chart rows in rupees (see BusinessLoanPrintView); null if unavailable.
  emiSchedule = null,
  // API_BASE_URL = '',
}) => {
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '';
    return `${Number(value).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatAddress = (data, type) => {
    const prefix = type === 'permanent' ? 'permanent' : 'current';
    const parts = [
      data[`${prefix}DoorNo`] || data[`${prefix}DoorNumber`],
      data[`${prefix}StreetAddress`],
      data[`${prefix}Village`],
      data[`${prefix}City`],
      type === 'permanent' ? data[`${prefix}District`] : null,
      data[`${prefix}State`],
      `${data[`${prefix}PostalCode`] || ''}`
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : '';
  };

  const getOrdinalSuffix = (day) => {
    if (!day) return '';
    const j = day % 10, k = day % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  // First 4 instalments of the real loan chart. EMIs vary month to month on
  // REGULAR/1.61/1.41 loans, so "Monthly EMI" shows the first instalment.
  const emiRows = (emiSchedule || []).slice(0, 4).map((row) => [
    String(row.dueNo),
    formatCurrency(row.principal),
    formatCurrency(row.interest),
    formatCurrency(row.total),
    formatCurrency(row.outstanding)
  ]);
  const monthlyEmi = emiSchedule?.length ? emiSchedule[0].total : proposedLoanDetailsData?.monthlyEmi;

  const ProfileSection = () => {
    const profileImageFilename = personalDetailsData?.customerProfileImage;
    console.log('Profile Image Filename:', profileImageFilename);
    const imageUrl = profileImageFilename && API_BASE_URL
      ? `${API_BASE_URL}/images/${profileImageFilename}`
      : null;

    console.log('Profile Image Field:', profileImageFilename);
    console.log('API_BASE_URL:', API_BASE_URL);
    console.log('Constructed Image URL:', imageUrl);
    console.log('Personal Details:', JSON.stringify(personalDetailsData, null, 2));

    const isValidImage = profileImageFilename?.match(/.*\.(jpg|jpeg|png|gif|webp)$/i);

    return (
      <View style={styles.profileSection}>
        {imageUrl && isValidImage ? (
          <Image
            style={styles.profileImage}
            src={`${API_BASE_URL}/images/${personalDetailsData?.customerProfileImage}`}
            onError={(e) => console.error('Image Load Error:', e.message || e, 'URL:', imageUrl, 'Customer ID:', personalDetailsData?.customerUniqueId || 'N/A')}
          />
        ) : (
          <View style={styles.profileImagePlaceholder}>
            <Text style={{ fontSize: 8, textAlign: 'center', color: '#666' }}>
              {isValidImage === null ? 'No Image' : `Invalid Image: ${profileImageFilename || 'N/A'}`}
            </Text>
          </View>
        )}
        <View style={styles.profileInfo}>
          <Text style={[styles.profileText, { fontSize: 12, fontWeight: 'bold' }]}>
            {personalDetailsData?.name || 'N/A'}
          </Text>
          <Text style={styles.profileText}>
            <Text style={{ fontWeight: 'bold' }}>Customer ID: </Text>
            {personalDetailsData?.customerUniqueId || 'N/A'}
          </Text>
          <Text style={styles.profileText}>
            <Text style={{ fontWeight: 'bold' }}>Mobile: </Text>
            {personalDetailsData?.mobileNumber || 'N/A'}
          </Text>
          <Text style={styles.profileText}>
            <Text style={{ fontWeight: 'bold' }}>Date of Birth: </Text>
            {personalDetailsData?.dob || 'N/A'}
          </Text>
          <Text style={styles.profileText}>
            <Text style={{ fontWeight: 'bold' }}>Application Date: </Text>
            {new Date().toLocaleDateString()}
          </Text>
        </View>
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.companyName}>JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD</Text>
          <Text style={styles.documentTitle}>Business Loan Application</Text>
        </View>

        <ProfileSection />


        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Personal Information</Text>
          <View style={styles.table}>
            <TableRow cells={[
              "Date Of Birth", personalDetailsData?.dob || '',
              "Age", personalDetailsData?.age ? String(personalDetailsData.age) : '',
              "Phone No", personalDetailsData?.mobileNumber ? String(personalDetailsData.mobileNumber) : ''
            ]} />
            <TableRow cells={[
              "Aadhar No", verificationDetailsData?.aadharNo ? String(verificationDetailsData.aadharNo) : '',
              "Pan No", verificationDetailsData?.panNo || '',
              "Gender", personalDetailsData?.gender || ''
            ]} />
            <TableRow cells={[
              "Father's Name", personalDetailsData?.fatherName || '',
              "Marital Status", personalDetailsData?.maritalStatus || '',
              "Citizenship", personalDetailsData?.citizenship || ''
            ]} />
            <TableRow cells={[
              "Application ID", memberDetailsData?.ApplicationId || '',
              "EMI Date", proposedLoanDetailsData?.emiDate ? `${proposedLoanDetailsData.emiDate}${getOrdinalSuffix(proposedLoanDetailsData.emiDate)}` : '',
              "Customer ID", personalDetailsData?.customerUniqueId || ''
            ]} isLast={true} />
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Business Information</Text>
          <View style={styles.table}>
            <TableRow cells={[
              "Business Type", memberBusinessDetailsData?.sourceOfIncome || '',
              "Monthly Income", memberBusinessDetailsData?.monthlyBusinessIncome ? formatCurrency(memberBusinessDetailsData.monthlyBusinessIncome) : '',
              "State", memberBusinessDetailsData?.state || ''
            ]} />
            <TableRow cells={[
              "Nationality", memberBusinessDetailsData?.nationality || '',
              "Residence Type", memberDetailsData?.residenceType || '',
              "Staying Stability", memberDetailsData?.stayingStabilityInYears || memberDetailsData?.stayingStabilityInMonths ?
                `${memberDetailsData.stayingStabilityInYears || 0}Y ${memberDetailsData.stayingStabilityInMonths || 0}M` : ''
            ]} isLast={true} />
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Address Information</Text>
          <View style={styles.table}>
            <TableRow cells={[
              "Permanent Address", formatAddress(personalDetailsData, 'permanent')
            ]} />
            <TableRow cells={[
              "Current Address", formatAddress(personalDetailsData, 'current')
            ]} isLast={true} />
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Loan Information</Text>
          <View style={styles.table}>
            <TableRow cells={[
              "Loan Amount", formatINR(proposedLoanDetailsData?.loanAmount),
              "Monthly EMI", formatCurrency(monthlyEmi),
              "Interest Rate", proposedLoanDetailsData?.rateOfInterest ? `${proposedLoanDetailsData.rateOfInterest}%` : ''
            ]} />
            <TableRow cells={[
              "Tenure", proposedLoanDetailsData?.tenureInMonths ? `${proposedLoanDetailsData.tenureInMonths} months` : '',
              "Processing Fee", formatINR(proposedLoanDetailsData?.processingFee),
              "Total Amount", formatINR(proposedLoanDetailsData?.totalAmountPayable)
            ]} isLast={true} />
          </View>
        </View>
      </Page>

      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Nominee Information</Text>
          <View style={styles.table}>
            <TableRow cells={[
              "Name", nomineeDetailsData?.name || '',
              "Father's Name", nomineeDetailsData?.fatherName || '',
              "Gender", nomineeDetailsData?.gender || ''
            ]} />
            <TableRow cells={[
              "Date of Birth", nomineeDetailsData?.dob || '',
              "Mobile Number", nomineeDetailsData?.mobileNumber ? String(nomineeDetailsData.mobileNumber) : '',
              "Relationship", nomineeDetailsData?.relationship || ''
            ]} />
            <TableRow cells={[
              "Address", nomineeDetailsData?.address || '',
              "Aadhaar Number", nomineeDetailsData?.aadharNo || '',
              "ID Proof", nomineeDetailsData?.idProof || ''
            ]} isLast={true} />
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Bank Details</Text>
          <View style={styles.table}>
            <TableRow cells={[
              "Account Number", bankDetailsData?.accountNumber || '',
              "Account Holder", bankDetailsData?.accountHolderName || '',
              "IFSC Code", bankDetailsData?.ifscCode || ''
            ]} />
            <TableRow cells={[
              "Bank Name", bankDetailsData?.bankName || '',
              "Branch Name", bankDetailsData?.bankBranchName || '',
              "Account Type", bankDetailsData?.accountType || ''
            ]} isLast={true} />
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>EMI Schedule (First 4 Months)</Text>
          <View style={styles.emiTable}>
            <EMITableRow
              cells={["Month", "Principal ", "Interest ", "EMI ", "Balance "]}
              isHeader={true}
            />
            {emiRows.length > 0 ? emiRows.map((row, index) => (
              <EMITableRow
                key={index}
                cells={row}
                isLast={index === emiRows.length - 1}
              />
            )) : (
              <EMITableRow cells={["-", "Schedule unavailable", "-", "-", "-"]} isLast={true} />
            )}
          </View>
        </View>

        <View style={styles.signatures}>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Applicant's Signature</Text>
            <View style={styles.signatureLine} />
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Nominee's Signature</Text>
            <View style={styles.signatureLine} />
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Approving Officer</Text>
            <View style={styles.signatureLine} />
          </View>
        </View>

        <View style={styles.footer}>
          <Text>This document is valid until 30 days from the date of printing</Text>
          <Text>Document generated on: {new Date().toLocaleDateString()}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default BusinessLoanPDF;